import React, { useEffect, useMemo, useState } from "react";
import api from "../../apis/EdisonApi";
import { toast } from "react-toastify";

const modes = [
	{ value: "hg", label: "HG" },
	{ value: "away", label: "Away" },
	{ value: "schedule", label: "Schedule" },
];

const normalizeMode = (mode) => String(mode || "").trim().toLowerCase();

const SubmitNetatmoData = ({ mode, setpoint, onUpdated }) => {
	const [selection, setSelection] = useState(null);
	const [targetTemp, setTargetTemp] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		const normalizedMode = normalizeMode(mode);
		setSelection(modes.some(({ value }) => value === normalizedMode) ? normalizedMode : null);

		if (setpoint !== undefined && setpoint !== null) {
			setTargetTemp(String(setpoint));
		}
	}, [mode, setpoint]);

	const isManual = selection === null;
	const numericTarget = Number(targetTemp);
	const isTargetValid = targetTemp !== "" && numericTarget >= 5 && numericTarget <= 28;
	const targetError = isManual && targetTemp !== "" && !isTargetValid;

	const submitLabel = useMemo(
		() => (selection ? "Apply mode" : "Apply setpoint"),
		[selection]
	);

	const selectMode = (nextMode) => {
		setSelection((currentMode) => currentMode === nextMode ? null : nextMode);
	};

	const submitData = async (event) => {
		event.preventDefault();

		if (!selection && !isTargetValid) {
			toast.info("Enter a temperature between 5 and 28 °C");
			return;
		}

		const endpoint = selection ? "/netatmo/therm-mode" : "/netatmo/therm-point";
		const body = selection
			? { mode: selection }
			: { mode: "manual", temp: numericTarget };

		try {
			setIsSaving(true);
			const response = await api.post(endpoint, body);

			if (response.status !== 200) {
				toast.warning("The thermostat provider did not accept the change");
				return;
			}

			toast.success(selection ? "Thermostat mode updated" : "Thermostat setpoint updated");
			await onUpdated?.();
		} catch {
			toast.error("Unable to update the Netatmo thermostat");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<form className="device-control-form" onSubmit={submitData}>
			<div className="mode-selector" role="group" aria-label="Thermostat mode">
				{modes.map(({ value, label }) => (
					<button
						className={`mode-option${selection === value ? " mode-option--active" : ""}`}
						key={value}
						type="button"
						onClick={() => selectMode(value)}
						aria-pressed={selection === value}
					>
						{label}
					</button>
				))}
			</div>

			<div className="device-control-field">
				<label htmlFor="setpoint_id">Setpoint °C</label>
				<input
					className={`form-control${targetError ? " form-control--invalid" : ""}`}
					id="setpoint_id"
					name="setpoint"
					type="number"
					min="5"
					max="28"
					step="0.5"
					value={targetTemp}
					disabled={!isManual}
					onChange={(event) => setTargetTemp(event.target.value)}
					aria-invalid={targetError}
				/>
				<p className="device-control-field__help">
					{isManual
						? "Manual mode: enter a setpoint between 5 and 28 °C."
						: "The selected preset manages the setpoint; select it again to enter a manual value."}
				</p>
			</div>

			<div className="device-control-actions">
				<button
					className="btn btn-primary device-action-button"
					type="submit"
					disabled={isSaving || (isManual && !isTargetValid)}
				>
					{isSaving ? "Saving…" : submitLabel}
				</button>
			</div>
		</form>
	);
};

export default SubmitNetatmoData;
